'use client';

import UiIcon from '@/components/ui/UiIcon';
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import Button from './ui/Button';
import Card from './ui/Card';
import { cropImage, createImagePreview, getImageDimensions } from '../lib/imageUtils';

interface ImageCropperProps {
  file: File;
  onCrop: (croppedFile: File) => void;
  onCancel: () => void;
  aspectRatio?: number; // ширина/высота, например 16/9 или 1 для квадрата
  // Комментарий на русском: режим влияет на UI (для аватара — минимум лишнего).
  mode?: 'default' | 'avatar';
  // Комментарий на русском: заголовок окна.
  title?: string;
  // Комментарий на русском: текст основной кнопки (адаптируется под задачу: "Установить фото", "Загрузить", и т.п.).
  confirmText?: string;
  // Комментарий на русском: разрешить кнопку "без обрезки" (для аватара обычно выключаем).
  allowSkip?: boolean;
  // Комментарий на русском: текст кнопки "без обрезки".
  skipText?: string;
  // Комментарий на русском: включить простые “телеграмм‑правки” (яркость/контраст/насыщенность) для фото перед отправкой.
  enableAdjustments?: boolean;
  cropPresets?: Array<{
    name: string;
    ratio: number;
  }>;
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export default function ImageCropper({ 
  file, 
  onCrop, 
  onCancel, 
  aspectRatio,
  mode = 'default',
  title,
  confirmText,
  allowSkip,
  skipText,
  enableAdjustments,
  cropPresets = [
    { name: 'Квадрат 1:1', ratio: 1 },
    { name: 'Прямоугольник 16:9', ratio: 16/9 },
    { name: 'Прямоугольник 4:3', ratio: 4/3 },
    { name: 'Вертикальный 3:4', ratio: 3/4 },
    { name: 'Свободный', ratio: 0 }
  ]
}: ImageCropperProps) {
  const isAvatar = mode === 'avatar';
  const enableAdjust = !!enableAdjustments && !isAvatar;
  const effectiveAllowSkip = typeof allowSkip === 'boolean' ? allowSkip : !isAvatar;
  const effectiveTitle = (title || '').trim() || (isAvatar ? 'Фото профиля' : 'Обрезка изображения');
  const effectiveConfirmText = (confirmText || '').trim() || (isAvatar ? 'Установить фото' : 'Обрезать');
  const effectiveSkipText = (skipText || '').trim() || 'Загрузить без обрезки';

  // Комментарий на русском: определяем мобильное устройство для iOS-like интерфейса.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  const [compressEnabled, setCompressEnabled] = useState(true);
  useEffect(() => {
    setCompressEnabled(true);
  }, [file]);

  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [cropArea, setCropArea] = useState<CropArea>({ x: 0, y: 0, width: 200, height: 200 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  // Комментарий на русском: по умолчанию для произвольных изображений делаем свободное кадрирование.
  const [selectedRatio, setSelectedRatio] = useState<number>(
    typeof aspectRatio === 'number' ? aspectRatio : (isAvatar ? 1 : 0)
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [dragMode, setDragMode] = useState<'move' | 'resize' | 'pinch' | null>(null);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  // Комментарий на русском: курсор на десктопе должен “подсказывать”, где resize/move.
  const [hoverCursor, setHoverCursor] = useState<string>('crosshair');
  const hoverCursorRef = useRef<string>('crosshair');
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Комментарий на русском: pointer events для мобильных (touch) и десктопа (mouse) одним кодом.
  const primaryPointerIdRef = useRef<number | null>(null);
  const secondaryPointerIdRef = useRef<number | null>(null);
  const pointerPosRef = useRef(new Map<number, { x: number; y: number }>());
  const pinchStartRef = useRef<null | { distance: number; centerX: number; centerY: number; crop: CropArea }>(null);

  // ===== "Правки" как в Telegram (упрощённо) =====
  // Комментарий на русском: на мобильных изначально показываем предпросмотр, режим обрезки/правки включается по кнопке.
  const [mobileMode, setMobileMode] = useState<'preview' | 'crop' | 'adjust'>('preview');
  const [tab, setTab] = useState<'crop' | 'adjust'>('crop');
  const [brightness, setBrightness] = useState<number>(100);
  const [contrast, setContrast] = useState<number>(100);
  const [saturation, setSaturation] = useState<number>(100);
  const filterCss = useMemo(() => {
    if (!enableAdjust) return 'none';
    const b = Math.max(0, Math.min(200, Math.round(brightness)));
    const c = Math.max(0, Math.min(200, Math.round(contrast)));
    const s = Math.max(0, Math.min(300, Math.round(saturation)));
    if (b === 100 && c === 100 && s === 100) return 'none';
    return `brightness(${b}%) contrast(${c}%) saturate(${s}%)`;
  }, [brightness, contrast, enableAdjust, saturation]);
  const hasEdits = enableAdjust && filterCss !== 'none';

  const applyFiltersToFile = useCallback(
    async (inFile: File): Promise<File> => {
      if (!enableAdjust || filterCss === 'none') return inFile;
      if (typeof document === 'undefined') return inFile;

      const outType = (() => {
        const t = String(inFile.type || '').toLowerCase();
        if (t === 'image/png' || t === 'image/webp' || t === 'image/jpeg') return t;
        return 'image/jpeg';
      })();
      const ext = outType === 'image/png' ? 'png' : outType === 'image/webp' ? 'webp' : 'jpg';
      const base = (() => {
        const n = String(inFile.name || 'photo').trim() || 'photo';
        const noExt = n.replace(/\.[^/.]+$/, '');
        return noExt || 'photo';
      })();
      const outName = `${base}_edit.${ext}`;

      const url = URL.createObjectURL(inFile);
      try {
        const img = new Image();
        img.decoding = 'async';
        const loaded = new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Image load failed'));
        });
        img.src = url;
        await loaded;

        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width || 0;
        canvas.height = img.naturalHeight || img.height || 0;
        if (canvas.width <= 0 || canvas.height <= 0) return inFile;

        const ctx = canvas.getContext('2d');
        if (!ctx) return inFile;
        ctx.filter = filterCss;
        ctx.drawImage(img, 0, 0);
        ctx.filter = 'none';

        const blob = await new Promise<Blob>((resolve, reject) => {
          if (outType === 'image/png') {
            canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), outType);
          } else {
            canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), outType, 0.92);
          }
        });

        return new File([blob], outName, { type: outType });
      } catch (e) {
        console.error('Ошибка применения фильтров:', e);
        return inFile;
      } finally {
        try {
          URL.revokeObjectURL(url);
        } catch {}
      }
    },
    [enableAdjust, filterCss]
  );

  // Загрузка изображения (превью + размеры). Делаем это только при смене файла, чтобы не подвешивать UI.
  useEffect(() => {
    let previewUrl: string | null = null;
    const loadImage = async () => {
      try {
        const preview = await createImagePreview(file);
        const dimensions = await getImageDimensions(file);
        
        previewUrl = preview;
        setImagePreview(preview);
        setImageDimensions(dimensions);
        
        // Устанавливаем начальную область обрезки в центре
        const initialSize = Math.min(dimensions.width, dimensions.height) * 0.7;
        setCropArea({
          x: (dimensions.width - initialSize) / 2,
          y: (dimensions.height - initialSize) / 2,
          width: initialSize,
          height: selectedRatio ? initialSize / selectedRatio : initialSize
        });
      } catch (error) {
        console.error('Ошибка загрузки изображения:', error);
      }
    };

    loadImage();
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [file]);

  // Отрисовка превью с областью обрезки
  useEffect(() => {
    if (!imagePreview || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Масштабируем изображение для отображения
      const containerWidth = containerRef.current?.clientWidth || 600;
      const maxCanvasH =
        typeof window !== 'undefined'
          ? Math.max(260, Math.min(520, Math.floor(window.innerHeight * 0.55)))
          : 400;
      const scale = Math.min(containerWidth / imageDimensions.width, maxCanvasH / imageDimensions.height);
      
      canvas.width = imageDimensions.width * scale;
      canvas.height = imageDimensions.height * scale;
      
      // Рисуем изображение
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // Комментарий на русском: применяем фильтры к превью (чтобы кроп соответствовал финалу).
        ctx.filter = enableAdjust && filterCss !== 'none' ? filterCss : 'none';
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        ctx.filter = 'none';
      }
      
      // Рисуем область обрезки
      if (ctx) {
        const accent = isAvatar ? 'rgba(255,255,255,0.9)' : '#3b82f6';
        const handleColor = isAvatar ? 'rgba(255,255,255,0.95)' : '#3b82f6';

        // Сначала рисуем затемнение
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Затем вырезаем область обрезки
        ctx.globalCompositeOperation = 'destination-out';
        if (isAvatar) {
          const cx = (cropArea.x + cropArea.width / 2) * scale;
          const cy = (cropArea.y + cropArea.height / 2) * scale;
          const r = Math.min(cropArea.width, cropArea.height) * scale / 2;
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(
            cropArea.x * scale,
            cropArea.y * scale,
            cropArea.width * scale,
            cropArea.height * scale
          );
        }
        
        // Возвращаем нормальный режим композиции
        ctx.globalCompositeOperation = 'source-over';
        
        // Рисуем рамку области обрезки
        ctx.strokeStyle = accent;
        ctx.lineWidth = 2;
        if (isAvatar) {
          const cx = (cropArea.x + cropArea.width / 2) * scale;
          const cy = (cropArea.y + cropArea.height / 2) * scale;
          const r = Math.min(cropArea.width, cropArea.height) * scale / 2;
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.stroke();
        } else {
          ctx.strokeRect(
            cropArea.x * scale,
            cropArea.y * scale,
            cropArea.width * scale,
            cropArea.height * scale
          );
        }
        
        // Рисуем углы для изменения размера
        const cornerSize = isAvatar ? 12 : 8;
        ctx.fillStyle = handleColor;
        const corners = [
          { x: cropArea.x * scale - cornerSize/2, y: cropArea.y * scale - cornerSize/2 },
          { x: (cropArea.x + cropArea.width) * scale - cornerSize/2, y: cropArea.y * scale - cornerSize/2 },
          { x: cropArea.x * scale - cornerSize/2, y: (cropArea.y + cropArea.height) * scale - cornerSize/2 },
          { x: (cropArea.x + cropArea.width) * scale - cornerSize/2, y: (cropArea.y + cropArea.height) * scale - cornerSize/2 }
        ];
        
        corners.forEach(corner => {
          ctx.fillRect(corner.x, corner.y, cornerSize, cornerSize);
        });
      }
    };

    img.src = imagePreview;
  }, [imagePreview, cropArea, imageDimensions, enableAdjust, filterCss, isAvatar, mobileMode]);

  // Обработка перетаскивания области обрезки
  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!canvasRef.current) return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    
    const rect = canvasRef.current.getBoundingClientRect();
    const containerWidth = containerRef.current?.clientWidth || 600;
    const maxCanvasH =
      typeof window !== 'undefined'
        ? Math.max(260, Math.min(520, Math.floor(window.innerHeight * 0.55)))
        : 400;
    const scale = Math.min(containerWidth / imageDimensions.width, maxCanvasH / imageDimensions.height);
    
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;

    // Сохраняем позицию указателя в координатах изображения (нужно для pinch).
    pointerPosRef.current.set(e.pointerId, { x, y });

    // Комментарий на русском: второй палец => pinch масштабирование (лучшие практики для тач).
    if (
      primaryPointerIdRef.current != null &&
      secondaryPointerIdRef.current == null &&
      e.pointerType === 'touch'
    ) {
      secondaryPointerIdRef.current = e.pointerId;
      const p1 = pointerPosRef.current.get(primaryPointerIdRef.current);
      const p2 = pointerPosRef.current.get(secondaryPointerIdRef.current);
      if (p1 && p2) {
        const dx = p1.x - p2.x;
        const dy = p1.y - p2.y;
        const dist = Math.hypot(dx, dy);
        const cx = (p1.x + p2.x) / 2;
        const cy = (p1.y + p2.y) / 2;
        pinchStartRef.current = {
          distance: Math.max(0.0001, dist),
          centerX: cx,
          centerY: cy,
          crop: cropArea,
        };
        setIsDragging(true);
        setDragMode('pinch');
        setResizeHandle(null);
      }
      return;
    }

    // Если это первый указатель — регистрируем как основной.
    if (primaryPointerIdRef.current == null) {
      primaryPointerIdRef.current = e.pointerId;
    } else {
      // Если уже есть основной и это не pinch — игнорируем.
      return;
    }
    
    // Проверяем, попали ли в углы для изменения размера
    // Комментарий на русском: “углы” должны быть кликабельны в ПИКСЕЛЯХ экрана.
    // Если считать в координатах картинки, то при большом изображении (маленький scale) зона станет микроскопической.
    // Также: на тач-экранах делаем минимум 44px (iOS HIG).
    const cornerHitPx =
      e.pointerType === 'touch'
        ? (isAvatar ? 56 : 44)
        : (isAvatar ? 40 : 28);
    const cornerHit = cornerHitPx / Math.max(0.0001, scale);
    const corners = [
      { x: cropArea.x, y: cropArea.y, handle: 'nw' },
      { x: cropArea.x + cropArea.width, y: cropArea.y, handle: 'ne' },
      { x: cropArea.x, y: cropArea.y + cropArea.height, handle: 'sw' },
      { x: cropArea.x + cropArea.width, y: cropArea.y + cropArea.height, handle: 'se' }
    ];
    
    for (const corner of corners) {
      if (
        x >= corner.x - cornerHit/2 && 
        x <= corner.x + cornerHit/2 && 
        y >= corner.y - cornerHit/2 && 
        y <= corner.y + cornerHit/2
      ) {
        setIsDragging(true);
        setDragMode('resize');
        setResizeHandle(corner.handle);
        setDragStart({ x, y });
        return;
      }
    }
    
    // Проверяем, попали ли в область обрезки для перемещения
    if (
      x >= cropArea.x && 
      x <= cropArea.x + cropArea.width && 
      y >= cropArea.y && 
      y <= cropArea.y + cropArea.height
    ) {
      setIsDragging(true);
      setDragMode('move');
      setDragStart({ x: x - cropArea.x, y: y - cropArea.y });
    }
  }, [cropArea, imageDimensions, isAvatar]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;
    e.preventDefault();
    
    const rect = canvasRef.current.getBoundingClientRect();
    const containerWidth = containerRef.current?.clientWidth || 600;
    const maxCanvasH =
      typeof window !== 'undefined'
        ? Math.max(260, Math.min(520, Math.floor(window.innerHeight * 0.55)))
        : 400;
    const scale = Math.min(containerWidth / imageDimensions.width, maxCanvasH / imageDimensions.height);
    
    // Комментарий на русском: clamp координат указателя в пределах изображения,
    // иначе при уходе курсора “за картинку” в resize-режиме можно получить отрицательные размеры.
    const xRaw = (e.clientX - rect.left) / scale;
    const yRaw = (e.clientY - rect.top) / scale;
    const x = Math.max(0, Math.min(xRaw, imageDimensions.width));
    const y = Math.max(0, Math.min(yRaw, imageDimensions.height));

    // Обновляем позицию указателя (для pinch).
    pointerPosRef.current.set(e.pointerId, { x, y });

    // Комментарий на русском: десктоп/pen — показываем правильный курсор даже без клика.
    if (!isDragging) {
      if (e.pointerType !== 'touch') {
        const containerWidth2 = containerRef.current?.clientWidth || 600;
        const maxCanvasH2 =
          typeof window !== 'undefined'
            ? Math.max(260, Math.min(520, Math.floor(window.innerHeight * 0.55)))
            : 400;
        const scale2 = Math.min(containerWidth2 / imageDimensions.width, maxCanvasH2 / imageDimensions.height);
        const cornerHitPx = isAvatar ? 40 : 28;
        const cornerHit = cornerHitPx / Math.max(0.0001, scale2);
        const corners = [
          { x: cropArea.x, y: cropArea.y, handle: 'nw' as const },
          { x: cropArea.x + cropArea.width, y: cropArea.y, handle: 'ne' as const },
          { x: cropArea.x, y: cropArea.y + cropArea.height, handle: 'sw' as const },
          { x: cropArea.x + cropArea.width, y: cropArea.y + cropArea.height, handle: 'se' as const },
        ];
        let next = 'crosshair';
        for (const c of corners) {
          if (x >= c.x - cornerHit / 2 && x <= c.x + cornerHit / 2 && y >= c.y - cornerHit / 2 && y <= c.y + cornerHit / 2) {
            next = c.handle === 'nw' || c.handle === 'se' ? 'nwse-resize' : 'nesw-resize';
            break;
          }
        }
        if (next === 'crosshair') {
          const inside =
            x >= cropArea.x &&
            x <= cropArea.x + cropArea.width &&
            y >= cropArea.y &&
            y <= cropArea.y + cropArea.height;
          if (inside) next = 'grab';
        }
        if (hoverCursorRef.current !== next) {
          hoverCursorRef.current = next;
          setHoverCursor(next);
        }
      }
      return;
    }

    if (dragMode === 'pinch') {
      const p1id = primaryPointerIdRef.current;
      const p2id = secondaryPointerIdRef.current;
      const start = pinchStartRef.current;
      if (!p1id || !p2id || !start) return;
      const p1 = pointerPosRef.current.get(p1id);
      const p2 = pointerPosRef.current.get(p2id);
      if (!p1 || !p2) return;

      const cx = (p1.x + p2.x) / 2;
      const cy = (p1.y + p2.y) / 2;
      const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
      const scaleFactor = Math.max(0.0001, dist) / Math.max(0.0001, start.distance);

      const startCrop = start.crop;
      const startCropCenterX = startCrop.x + startCrop.width / 2;
      const startCropCenterY = startCrop.y + startCrop.height / 2;
      const deltaCenterX = cx - start.centerX;
      const deltaCenterY = cy - start.centerY;
      const newCenterX = startCropCenterX + deltaCenterX;
      const newCenterY = startCropCenterY + deltaCenterY;

      const imgW = imageDimensions.width;
      const imgH = imageDimensions.height;
      const minSize = 50;

      let nextW = startCrop.width;
      let nextH = startCrop.height;

      if (selectedRatio > 0) {
        const ratio = selectedRatio;
        const maxHalfW = Math.max(0, Math.min(newCenterX, imgW - newCenterX));
        const maxHalfH = Math.max(0, Math.min(newCenterY, imgH - newCenterY));
        const widthMaxRaw = Math.min(2 * maxHalfW, 2 * maxHalfH * ratio);
        const widthMax = Math.max(1, Number.isFinite(widthMaxRaw) ? widthMaxRaw : 1);
        const widthMin = Math.min(Math.max(1, minSize), widthMax);
        const desiredW = startCrop.width * scaleFactor;
        nextW = Math.min(Math.max(desiredW, widthMin), widthMax);
        nextH = Math.max(1, nextW / ratio);
      } else {
        const maxHalfW = Math.max(0, Math.min(newCenterX, imgW - newCenterX));
        const maxHalfH = Math.max(0, Math.min(newCenterY, imgH - newCenterY));
        const widthMax = Math.max(1, 2 * maxHalfW);
        const heightMax = Math.max(1, 2 * maxHalfH);
        const sMax = Math.max(0.0001, Math.min(widthMax / Math.max(1, startCrop.width), heightMax / Math.max(1, startCrop.height)));
        const sMin = Math.max(0.0001, minSize / Math.max(1, Math.min(startCrop.width, startCrop.height)));
        const sLo = Math.min(sMin, sMax);
        const s = Math.min(Math.max(scaleFactor, sLo), sMax);
        nextW = Math.max(1, startCrop.width * s);
        nextH = Math.max(1, startCrop.height * s);
      }

      let nx = newCenterX - nextW / 2;
      let ny = newCenterY - nextH / 2;
      nx = Math.max(0, Math.min(nx, imgW - nextW));
      ny = Math.max(0, Math.min(ny, imgH - nextH));

      setCropArea({ x: nx, y: ny, width: nextW, height: nextH });
      return;
    }

    // Для move/resize слушаем только основной указатель.
    if (primaryPointerIdRef.current !== e.pointerId) return;
    
    if (dragMode === 'move') {
      const newX = Math.max(0, Math.min(x - dragStart.x, imageDimensions.width - cropArea.width));
      const newY = Math.max(0, Math.min(y - dragStart.y, imageDimensions.height - cropArea.height));
      setCropArea(prev => ({ ...prev, x: newX, y: newY }));
    } else if (dragMode === 'resize' && resizeHandle) {
      const deltaX = x - dragStart.x;
      const deltaY = y - dragStart.y;
      
      setCropArea(prev => {
        let newArea = { ...prev };
        
        switch (resizeHandle) {
          case 'se': // Юго-восточный угол
            newArea.width = Math.max(50, prev.width + deltaX);
            if (selectedRatio > 0) {
              newArea.height = newArea.width / selectedRatio;
            } else {
              newArea.height = Math.max(50, prev.height + deltaY);
            }
            break;
          case 'sw': // Юго-западный угол
            newArea.x = Math.max(0, prev.x + deltaX);
            newArea.width = Math.max(50, prev.width - deltaX);
            if (selectedRatio > 0) {
              newArea.height = newArea.width / selectedRatio;
            } else {
              newArea.height = Math.max(50, prev.height + deltaY);
            }
            break;
          case 'ne': // Северо-восточный угол
            newArea.width = Math.max(50, prev.width + deltaX);
            if (selectedRatio > 0) {
              newArea.height = newArea.width / selectedRatio;
              newArea.y = Math.max(0, prev.y - (newArea.height - prev.height));
            } else {
              newArea.y = Math.max(0, prev.y + deltaY);
              newArea.height = Math.max(50, prev.height - deltaY);
            }
            break;
          case 'nw': // Северо-западный угол
            newArea.x = Math.max(0, prev.x + deltaX);
            newArea.width = Math.max(50, prev.width - deltaX);
            if (selectedRatio > 0) {
              newArea.height = newArea.width / selectedRatio;
              newArea.y = Math.max(0, prev.y - (newArea.height - prev.height));
            } else {
              newArea.y = Math.max(0, prev.y + deltaY);
              newArea.height = Math.max(50, prev.height - deltaY);
            }
            break;
        }

        const minSize = 50;
        const imgW = imageDimensions.width;
        const imgH = imageDimensions.height;

        // Комментарий на русском: фиксируем aspectRatio корректно и без “ломания” (важно для аватаров 1:1),
        // сохраняя противоположный угол как якорь.
        if (selectedRatio > 0) {
          const ratio = selectedRatio;
          const handle = resizeHandle as 'se' | 'sw' | 'ne' | 'nw';
          const ax =
            handle === 'se' || handle === 'ne' ? prev.x : (prev.x + prev.width);
          const ay =
            handle === 'se' || handle === 'sw' ? prev.y : (prev.y + prev.height);

          const widthMinBase = Math.max(1, minSize);
          const widthMin = Math.max(widthMinBase, widthMinBase * ratio);

          let widthMaxRaw = 0;
          if (handle === 'se') {
            widthMaxRaw = Math.min(imgW - ax, (imgH - ay) * ratio);
          } else if (handle === 'sw') {
            widthMaxRaw = Math.min(ax, (imgH - ay) * ratio);
          } else if (handle === 'ne') {
            widthMaxRaw = Math.min(imgW - ax, ay * ratio);
          } else {
            widthMaxRaw = Math.min(ax, ay * ratio);
          }

          const widthMax = Math.max(1, Number.isFinite(widthMaxRaw) ? widthMaxRaw : 1);
          const widthMinClamped = Math.min(Math.max(1, widthMin), widthMax);
          const desiredW = Math.max(1, newArea.width);
          const w = Math.min(Math.max(desiredW, widthMinClamped), widthMax);
          const h = w / ratio;

          if (handle === 'se') {
            newArea = { x: ax, y: ay, width: w, height: h };
          } else if (handle === 'sw') {
            newArea = { x: ax - w, y: ay, width: w, height: h };
          } else if (handle === 'ne') {
            newArea = { x: ax, y: ay - h, width: w, height: h };
          } else {
            newArea = { x: ax - w, y: ay - h, width: w, height: h };
          }
        } else {
          // Свободное кадрирование — просто держим в пределах картинки.
          newArea.width = Math.max(minSize, newArea.width);
          newArea.height = Math.max(minSize, newArea.height);
          newArea.x = Math.max(0, Math.min(newArea.x, imgW - minSize));
          newArea.y = Math.max(0, Math.min(newArea.y, imgH - minSize));
          newArea.width = Math.max(1, Math.min(newArea.width, imgW - newArea.x));
          newArea.height = Math.max(1, Math.min(newArea.height, imgH - newArea.y));
        }
        
        return newArea;
      });
      
      // Обновляем начальную точку для следующего движения
      setDragStart({ x, y });
    }
  }, [isDragging, dragStart, cropArea, imageDimensions, dragMode, resizeHandle, selectedRatio]);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    // Убираем указатель из карты
    pointerPosRef.current.delete(e.pointerId);
    if (primaryPointerIdRef.current === e.pointerId) {
      primaryPointerIdRef.current = null;
    }
    if (secondaryPointerIdRef.current === e.pointerId) {
      secondaryPointerIdRef.current = null;
    }
    if (!primaryPointerIdRef.current || !secondaryPointerIdRef.current) {
      pinchStartRef.current = null;
    }
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    setIsDragging(false);
    setDragMode(null);
    setResizeHandle(null);
  }, []);

  // Изменение соотношения сторон
  const handleRatioChange = (ratio: number) => {
    setSelectedRatio(ratio);
    
    if (ratio > 0) {
      // Пересчитываем высоту с учетом нового соотношения
      const newHeight = cropArea.width / ratio;
      const maxHeight = imageDimensions.height - cropArea.y;
      
      if (newHeight <= maxHeight) {
        setCropArea(prev => ({ ...prev, height: newHeight }));
      } else {
        // Если не помещается по высоте, уменьшаем ширину
        const newWidth = maxHeight * ratio;
        setCropArea(prev => ({ ...prev, width: newWidth, height: maxHeight }));
      }
    }
  };

  // Применение обрезки
  const handleCrop = async () => {
    // Комментарий на русском: на мобильных в режиме preview — пропускаем обрезку (используем оригинал с правками).
    if (isMobile && mobileMode === 'preview') {
      return handleSkipCrop();
    }

    setIsProcessing(true);
    try {
      // Комментарий на русском: дополнительно "подстраховываем" область кропа,
      // чтобы никогда не отдать cropImage отрицательные/нулевые размеры (иначе canvas/toBlob может вернуть null).
      const safe = (() => {
        const minSize = 1;
        const imgW = imageDimensions.width || 0;
        const imgH = imageDimensions.height || 0;
        let x = Number.isFinite(cropArea.x) ? cropArea.x : 0;
        let y = Number.isFinite(cropArea.y) ? cropArea.y : 0;
        let w = Number.isFinite(cropArea.width) ? cropArea.width : minSize;
        let h = Number.isFinite(cropArea.height) ? cropArea.height : minSize;

        x = Math.max(0, Math.min(x, Math.max(0, imgW - minSize)));
        y = Math.max(0, Math.min(y, Math.max(0, imgH - minSize)));

        if (selectedRatio > 0) {
          const ratio = selectedRatio;
          const maxW = Math.max(minSize, Math.min(imgW - x, (imgH - y) * ratio));
          w = Math.max(minSize, Math.min(w, maxW));
          h = Math.max(minSize, w / ratio);
        } else {
          w = Math.max(minSize, Math.min(w, Math.max(minSize, imgW - x)));
          h = Math.max(minSize, Math.min(h, Math.max(minSize, imgH - y)));
        }

        return {
          x: Math.round(x),
          y: Math.round(y),
          width: Math.round(w),
          height: Math.round(h),
        };
      })();

      const croppedFile = await cropImage(file, safe);

      // Комментарий на русском: перед загрузкой всегда оптимизируем (resize/compress),
      // чтобы не отправлять “сырые” тяжелые файлы на сервер.
      let outFile: File = croppedFile;
      // Комментарий на русском: правки (яркость/контраст/насыщенность) применяем к уже обрезанному файлу.
      if (hasEdits) {
        outFile = await applyFiltersToFile(outFile);
      }
      if (compressEnabled) {
        try {
          const { optimizeImage, compressImage } = await import('../lib/imageUtils');
          if (isAvatar) {
            // Комментарий на русском: для аватаров держим более строгий размер (быстрее грузится в списках/чатах).
            outFile = await compressImage(croppedFile, { maxWidth: 512, maxHeight: 512, quality: 0.88, format: 'jpeg' });
          } else {
            outFile = await optimizeImage(outFile);
          }
          if (!outFile || outFile.size === 0) outFile = croppedFile;
        } catch {
          outFile = croppedFile;
        }
      }

      onCrop(outFile);
    } catch (error) {
      console.error('Ошибка обрезки изображения:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Загрузка без обрезки
  const handleSkipCrop = async () => {
    setIsProcessing(true);
    try {
      let outFile: File = file;
      if (hasEdits) outFile = await applyFiltersToFile(outFile);
      if (compressEnabled) {
        // Оптимизируем изображение даже при пропуске обрезки
        const { optimizeImage } = await import('../lib/imageUtils');
        const optimizedFile = await optimizeImage(outFile);

        // Проверяем, что файл валидный
        if (!optimizedFile || optimizedFile.size === 0) {
          throw new Error('Оптимизированный файл пустой');
        }
        
        onCrop(optimizedFile);
      } else {
        if (!outFile || outFile.size === 0) {
          throw new Error('Файл пустой');
        }
        onCrop(outFile);
      }
    } catch (error) {
      console.error('Ошибка оптимизации изображения:', error);

      // Проверяем, что оригинальный файл валидный
      if (!file || file.size === 0) {
        console.error('Оригинальный файл тоже пустой');
        return;
      }
      
      // Если оптимизация не удалась, используем оригинальный файл
      onCrop(file);
    } finally {
      setIsProcessing(false);
    }
  };

  // Комментарий на русском: iOS-like иконки для мобильной версии.
  const CropIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2v4M18 18h4M6 18H3V6h15M21 18V6h-4"/>
    </svg>
  );
  const AdjustIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
    </svg>
  );
  const CheckIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
  const CloseIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
  const UndoIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7v6h6"/>
      <path d="M3 13a9 9 0 1 0 3-7.7L3 7"/>
    </svg>
  );

  // Комментарий на русском: сброс обрезки и правок к исходному состоянию.
  const handleMobileUndo = useCallback(() => {
    // Сбрасываем правки
    setBrightness(100);
    setContrast(100);
    setSaturation(100);
    // Сбрасываем область обрезки к начальной
    if (imageDimensions.width > 0 && imageDimensions.height > 0) {
      const initialSize = Math.min(imageDimensions.width, imageDimensions.height) * 0.7;
      setCropArea({
        x: (imageDimensions.width - initialSize) / 2,
        y: (imageDimensions.height - initialSize) / 2,
        width: initialSize,
        height: selectedRatio ? initialSize / selectedRatio : initialSize
      });
    }
    // Возвращаемся в режим предпросмотра
    setMobileMode('preview');
  }, [imageDimensions, selectedRatio]);

  // Комментарий на русском: проверка есть ли изменения (для показа кнопки undo).
  const hasMobileEdits = useMemo(() => {
    return brightness !== 100 || contrast !== 100 || saturation !== 100 || mobileMode !== 'preview';
  }, [brightness, contrast, saturation, mobileMode]);

  // Комментарий на русском: мобильная версия — fullscreen с нижней панелью как на iOS.
  if (isMobile) {
    return (
      <div className="fixed inset-0 bg-site-bg z-[11000] flex flex-col">
        {/* Область изображения */}
        <div className="flex-1 flex items-center justify-center overflow-hidden p-4" ref={containerRef}>
          {/* Режим предпросмотра — просто картинка */}
          {mobileMode === 'preview' && imagePreview && (
            <img
              src={imagePreview}
              alt="Предпросмотр"
              className="max-w-full max-h-full object-contain select-none rounded-lg"
              style={{ filter: filterCss === 'none' ? undefined : filterCss }}
              draggable={false}
            />
          )}
          
          {/* Режим обрезки — canvas с рамкой */}
          {mobileMode === 'crop' && (
            (() => {
              const active =
                isDragging
                  ? dragMode === 'resize'
                    ? resizeHandle === 'nw' || resizeHandle === 'se'
                      ? 'nwse-resize'
                      : 'nesw-resize'
                    : dragMode === 'move'
                      ? 'grabbing'
                      : dragMode === 'pinch'
                        ? 'grabbing'
                        : 'crosshair'
                  : hoverCursor;
              return (
                <canvas
                  ref={canvasRef}
                  className="max-w-full max-h-full"
                  style={{ touchAction: 'none', userSelect: 'none', cursor: active }}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerUp}
                />
              );
            })()
          )}
          
          {/* Режим правок — картинка с фильтрами */}
          {mobileMode === 'adjust' && imagePreview && (
            <img
              src={imagePreview}
              alt="Предпросмотр"
              className="max-w-full max-h-full object-contain select-none rounded-lg"
              style={{ filter: filterCss === 'none' ? undefined : filterCss }}
              draggable={false}
            />
          )}
        </div>

        {/* Слайдеры правок для мобильных */}
        {mobileMode === 'adjust' && (
          <div className="px-4 pb-2 space-y-2 bg-site-surface/80">
            <div className="flex items-center gap-3">
              <span className="text-xs text-site-secondary w-20">Яркость</span>
              <input type="range" min={50} max={150} value={brightness} onChange={(e) => setBrightness(Number(e.target.value) || 100)} className="flex-1" />
              <span className="text-xs text-site-text tabular-nums w-10 text-right">{Math.round(brightness)}%</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-site-secondary w-20">Контраст</span>
              <input type="range" min={50} max={150} value={contrast} onChange={(e) => setContrast(Number(e.target.value) || 100)} className="flex-1" />
              <span className="text-xs text-site-text tabular-nums w-10 text-right">{Math.round(contrast)}%</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-site-secondary w-20">Насыщенность</span>
              <input type="range" min={0} max={200} value={saturation} onChange={(e) => setSaturation(Number(e.target.value) || 100)} className="flex-1" />
              <span className="text-xs text-site-text tabular-nums w-10 text-right">{Math.round(saturation)}%</span>
            </div>
          </div>
        )}

        <div className="px-4 pb-2">
          <div className="flex items-center justify-between rounded-xl border border-site-border bg-site-text/5 px-3 py-2">
            <span className="text-xs text-site-secondary">Сжатие</span>
            <button
              type="button"
              role="switch"
              aria-checked={compressEnabled}
              onClick={() => setCompressEnabled((prev) => !prev)}
              className="inline-flex items-center gap-2 rounded-full px-2 py-1 focus-ring"
            >
              <span className="text-[11px] text-site-muted">
                {compressEnabled ? 'Вкл' : 'Выкл'}
              </span>
              <span
                className={[
                  'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                  compressEnabled ? 'bg-site-success/80' : 'bg-site-raised/70',
                ].join(' ')}
              >
                <span
                  className={[
                    'inline-block h-5 w-5 transform rounded-full bg-site-surface shadow transition-transform',
                    compressEnabled ? 'translate-x-5' : 'translate-x-1',
                  ].join(' ')}
                />
              </span>
            </button>
          </div>
        </div>

        {/* Нижняя панель iOS-style */}
        <div className="flex items-center justify-between px-4 py-3 bg-site-surface/90 border-t border-site-border safe-area-bottom">
          {/* Отмена слева */}
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className="w-12 h-12 flex items-center justify-center text-site-text/80 active:text-site-text/50 disabled:opacity-40"
            aria-label="Отмена"
          >
            <CloseIcon />
          </button>

          {/* Центральные инструменты */}
          <div className="flex items-center gap-2">
            {/* Кнопка undo — показываем если есть изменения */}
            {hasMobileEdits && (
              <button
                type="button"
                onClick={handleMobileUndo}
                className="w-10 h-10 flex items-center justify-center text-site-text/70 active:text-site-text/50 rounded-full bg-site-text/10"
                aria-label="Сбросить"
              >
                <UndoIcon />
              </button>
            )}
            
            {/* Кнопки режимов: обрезка и правка */}
            <div className="flex items-center gap-1 bg-site-text/10 rounded-full p-1">
              <button
                type="button"
                onClick={() => setMobileMode(mobileMode === 'crop' ? 'preview' : 'crop')}
                className={`w-11 h-11 flex items-center justify-center rounded-full transition-colors ${
                  mobileMode === 'crop' ? 'bg-site-surface text-site-text' : 'text-site-text/70'
                }`}
                aria-label="Обрезка"
              >
                <CropIcon />
              </button>
              {enableAdjust && (
                <button
                  type="button"
                  onClick={() => setMobileMode(mobileMode === 'adjust' ? 'preview' : 'adjust')}
                  className={`w-11 h-11 flex items-center justify-center rounded-full transition-colors ${
                    mobileMode === 'adjust' ? 'bg-site-surface text-site-text' : 'text-site-text/70'
                  }`}
                  aria-label="Правка"
                >
                  <AdjustIcon />
                </button>
              )}
            </div>
          </div>

          {/* Подтверждение справа */}
          <button
            type="button"
            onClick={handleCrop}
            disabled={isProcessing}
            className="w-12 h-12 flex items-center justify-center text-site-warning active:text-site-warning disabled:opacity-40"
            aria-label="Применить"
          >
            {isProcessing ? (
              <span className="w-6 h-6 border-2 border-site-warning/30 border-t-yellow-400 rounded-full animate-spin" />
            ) : (
              <CheckIcon />
            )}
          </button>
        </div>
      </div>
    );
  }

  // Комментарий на русском: десктопная версия — оригинальный интерфейс.
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[11000] p-4">
      <Card
        variant="glass"
        className={`${isAvatar ? 'max-w-2xl' : 'max-w-4xl'} w-full max-h-[90vh] overflow-auto`}
      >
        <div className={`${isAvatar ? 'p-4 sm:p-6' : 'p-6'}`}>
          {/* Заголовок */}
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h3 className={`${isAvatar ? 'text-lg' : 'text-xl'} font-bold text-site-text`}>{effectiveTitle}</h3>
            {!isAvatar && (
              <Button variant="ghost" onClick={onCancel} className="text-site-muted hover:text-site-text">
                ✕
              </Button>
            )}
          </div>

          {/* Вкладки (Crop / Adjust) */}
          {enableAdjust && (
            <div className="mb-4 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setTab('crop')}
                className={`px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${
                  tab === 'crop' ? 'ui-selected' : 'bg-site-surface/60 text-site-text hover:bg-site-raised/60'
                }`}
              >
                Обрезка
              </button>
              <button
                type="button"
                onClick={() => setTab('adjust')}
                className={`px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${
                  tab === 'adjust' ? 'ui-selected' : 'bg-site-surface/60 text-site-text hover:bg-site-raised/60'
                }`}
              >
                Правки
              </button>
              {hasEdits && <div className="ml-1 text-xs text-site-success/90">● Изменено</div>}
              <div className="flex-1" />
              {hasEdits && (
                <button
                  type="button"
                  onClick={() => {
                    setBrightness(100);
                    setContrast(100);
                    setSaturation(100);
                  }}
                  className="px-3 py-2 rounded-xl text-xs text-site-text hover:text-site-text bg-site-surface/60 hover:bg-site-raised/60 transition-colors"
                  title="Сбросить правки"
                >
                  Сброс
                </button>
              )}
            </div>
          )}

          {/* Выбор соотношения сторон — скрыто на мобильных */}
          {!isAvatar && (!enableAdjust || tab === 'crop') && (
            <div className="mb-6">
            <label className="block text-sm font-medium text-site-secondary mb-3">
              Соотношение сторон
            </label>
            <div className="flex flex-wrap gap-2">
              {cropPresets.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => handleRatioChange(preset.ratio)}
                  className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                    selectedRatio === preset.ratio
                      ? 'bg-site-accent text-site-on-accent'
                      : 'bg-site-raised text-site-secondary hover:bg-site-raised'
                  }`}
                >
                  {preset.name}
                </button>
              ))}
            </div>
            </div>
          )}

          {/* Область обрезки */}
          {(!enableAdjust || tab === 'crop') && (
            <div className={`${isAvatar ? 'mb-4' : 'mb-6'}`} ref={containerRef}>
              <div className="bg-site-surface rounded-lg p-4 flex justify-center">
                {(() => {
                  const active =
                    isDragging
                      ? dragMode === 'resize'
                        ? resizeHandle === 'nw' || resizeHandle === 'se'
                          ? 'nwse-resize'
                          : 'nesw-resize'
                        : dragMode === 'move'
                          ? 'grabbing'
                          : dragMode === 'pinch'
                            ? 'grabbing'
                            : 'crosshair'
                      : hoverCursor;
                  return (
                    <canvas
                      ref={canvasRef}
                      className="max-w-full border border-site-border-control rounded"
                      style={{ touchAction: 'none', userSelect: 'none', cursor: active }}
                      onPointerDown={handlePointerDown}
                      onPointerMove={handlePointerMove}
                      onPointerUp={handlePointerUp}
                      onPointerCancel={handlePointerUp}
                      onPointerLeave={() => {
                        if (hoverCursorRef.current !== 'crosshair') {
                          hoverCursorRef.current = 'crosshair';
                          setHoverCursor('crosshair');
                        }
                      }}
                    />
                  );
                })()}
              </div>
            </div>
          )}

          {enableAdjust && tab === 'adjust' && (
            <div className="mb-6">
              <div className="bg-site-surface rounded-lg p-4 flex justify-center">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Предпросмотр"
                    className="max-w-full max-h-[55vh] rounded-xl border border-site-border-control/40 object-contain select-none"
                    style={{ filter: filterCss === 'none' ? undefined : filterCss }}
                    draggable={false}
                  />
                ) : (
                  <div className="w-full max-w-[520px] aspect-[4/3] rounded-xl border border-site-border-control/40 bg-black/20 flex items-center justify-center">
                    <span className="w-6 h-6 border-2 border-site-border border-t-white/80 rounded-full animate-spin" />
                  </div>
                )}
              </div>

              <div className="mt-4 space-y-3">
                <div>
                  <div className="flex items-center justify-between text-xs text-site-secondary mb-1">
                    <span>Яркость</span>
                    <span className="tabular-nums text-site-text">{Math.round(brightness)}%</span>
                  </div>
                  <input
                    type="range"
                    min={50}
                    max={150}
                    value={brightness}
                    onChange={(e) => setBrightness(Number(e.target.value) || 100)}
                    className="w-full"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between text-xs text-site-secondary mb-1">
                    <span>Контраст</span>
                    <span className="tabular-nums text-site-text">{Math.round(contrast)}%</span>
                  </div>
                  <input
                    type="range"
                    min={50}
                    max={150}
                    value={contrast}
                    onChange={(e) => setContrast(Number(e.target.value) || 100)}
                    className="w-full"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between text-xs text-site-secondary mb-1">
                    <span>Насыщенность</span>
                    <span className="tabular-nums text-site-text">{Math.round(saturation)}%</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={200}
                    value={saturation}
                    onChange={(e) => setSaturation(Number(e.target.value) || 100)}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Информация */}
          {!isAvatar && (!enableAdjust || tab === 'crop') && (
            <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
            <div className="bg-site-surface/50 p-3 rounded-lg">
              <div className="text-site-muted">Исходный размер</div>
              <div className="text-site-text font-mono">
                {imageDimensions.width} × {imageDimensions.height}
              </div>
            </div>
            <div className="bg-site-surface/50 p-3 rounded-lg">
              <div className="text-site-muted">Размер обрезки</div>
              <div className="text-site-text font-mono">
                {Math.round(cropArea.width)} × {Math.round(cropArea.height)}
              </div>
            </div>
            </div>
          )}

           {/* Подсказка */}
           {!isAvatar && (!enableAdjust || tab === 'crop') && (
             <div className="mb-6 p-3 bg-site-accent/20 border border-site-accent/30 rounded-lg">
             <p className="text-site-accent text-sm">
               <UiIcon name="Lightbulb" /> Перетащите область обрезки для изменения положения.
               Перетащите углы для изменения размера. 
               Используйте кнопки соотношения сторон для изменения формата.
             </p>
             </div>
           )}

          <div className="mb-6 flex items-center justify-between rounded-xl border border-site-border bg-site-surface/40 px-3 py-2">
            <span className="text-sm text-site-text">Сжатие</span>
            <button
              type="button"
              role="switch"
              aria-checked={compressEnabled}
              onClick={() => setCompressEnabled((prev) => !prev)}
              className="inline-flex items-center gap-2 rounded-full px-2 py-1 focus-ring"
            >
              <span className="text-xs text-site-muted">
                {compressEnabled ? 'Вкл' : 'Выкл'}
              </span>
              <span
                className={[
                  'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                  compressEnabled ? 'bg-site-success/80' : 'bg-site-raised/70',
                ].join(' ')}
              >
                <span
                  className={[
                    'inline-block h-5 w-5 transform rounded-full bg-site-surface shadow transition-transform',
                    compressEnabled ? 'translate-x-5' : 'translate-x-1',
                  ].join(' ')}
                />
              </span>
            </button>
          </div>

          {/* Кнопки действий */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-wrap gap-3">
              <Button variant="ghost" onClick={onCancel} disabled={isProcessing}>
                Отмена
              </Button>
              {effectiveAllowSkip && (
                <Button
                  variant="outline"
                  onClick={handleSkipCrop}
                  disabled={isProcessing}
                  className="border-site-success text-site-success hover:bg-site-success/20"
                >
                  {effectiveSkipText}
                </Button>
              )}
            </div>
            <Button
              variant="primary"
              onClick={handleCrop}
              disabled={isProcessing}
              className="bg-site-action"
            >
              {isProcessing ? 'Обработка...' : effectiveConfirmText}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}





